import { useState, useCallback, useRef, useEffect, useContext } from "react";
import getAgGridHeight from "../../../utils/commonFunctions/getAgGridHeight";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { PaginationQuery, PaginationTypes } from "../../../@types/pagination.types";
import { CustomPagination } from "../../shared";
import { UIContext } from "../../../contexts";
import { useMediaQuery } from "@material-ui/core";

// import "ag-grid-community/dist/styles/ag-grid.css";
// import "ag-grid-community/dist/styles/ag-theme-alpine.css";

import getLimit from "../../../utils/pagination/getLimit";
import { dateTimeZoneConverter } from "../../../utils/commonFunctions/date";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../utils/api";

// Renders download links for whichever STL files exist on a scan. A scan reaches this
// grid only when at least one of the two URLs is present, so at least one link shows.
const StlLinksCellRenderer = ({ data }: { data: any }) => {
	const left = data?.left_stl_url;
	const right = data?.right_stl_url;
	return (
		<div style={{ display: "flex", gap: 12 }}>
			{left ? (
				<a href={left} target="_blank" rel="noopener noreferrer" download>
					Left STL
				</a>
			) : null}
			{right ? (
				<a href={right} target="_blank" rel="noopener noreferrer" download>
					Right STL
				</a>
			) : null}
		</div>
	);
};

const LabScans = () => {
	const { setDashboardHeader } = useContext(UIContext);
	const [scans, setScans] = useState<any[]>([]);
	const [gridHeight, setGridHeight] = useState("400px");
	const [agGridAPI, setAgGridAPI] = useState<GridApi | null>(null);
	// eslint-disable-next-line no-unused-vars
	const [agGridColumnAPI, setAgGridColumnAPI] = useState<ColumnApi | null>(null);
	const agGridRef = useRef<any>(null);
	const [limit, setLimit] = useState<number | null>(null);
	const [pagination, setPagination] = useState<PaginationTypes>({
		currentPage: 1,
		pageCount: undefined
	});
	const { currentPage, pageCount } = pagination;
	const xl = useMediaQuery("(min-width:1600px)");
	const lg = useMediaQuery("(min-width:1200px)");
	const md = useMediaQuery("(min-width:800px)");
	const sm = useMediaQuery("(min-width:600px)");
	const xs = useMediaQuery("(min-width:400px)");
	const _limit = getLimit(xl, lg, md, sm, xs);

	const handleAgGridReady = (params: GridReadyEvent) => {
		// eslint-disable-next-line no-unused-vars
		const { api, columnApi } = params;
		setAgGridAPI(Object.assign({}, agGridAPI, api));
		setAgGridColumnAPI(columnApi);
	};

	const handleFirstDataRendered = (params: any) => {
		const { api } = params;
		api.sizeColumnsToFit();
	};

	const handlePageChange = useCallback(
		(event: React.ChangeEvent<unknown>, page: number) => {
			setPagination(
				Object.assign({}, pagination, {
					currentPage: page
				})
			);
		},
		[pagination]
	);

	const handleDateCellRenderer = ({ value }: { value: string }) => dateTimeZoneConverter(value);

	const handleCustomerNameCellRenderer = ({ data }: { data: any }) => {
		const first = data?.customer?.first_name ?? "";
		const last = data?.customer?.last_name ?? "";
		const name = `${first} ${last}`.trim();
		return <div>{name || "-"}</div>;
	};

	const getStlScanList = useCallback(async () => {
		if (typeof limit === "number" && typeof pagination.currentPage === "number") {
			const paginationQuery: PaginationQuery = {
				limit: limit,
				page: pagination.currentPage
			};
			const result = await trackPromise(api.scan.getPaginatedStlScanList(paginationQuery));
			if (result && result.scans) {
				setScans(result.scans);
				setPagination((prev) => Object.assign({}, prev, { pageCount: result.totalPages }));
			} else {
				setScans([]);
			}
		}
	}, [limit, pagination.currentPage]);

	useEffect(() => {
		if (_limit >= 0) {
			setLimit(_limit);
			getStlScanList();
		}
		setDashboardHeader("STL Scans");
	}, [_limit, setDashboardHeader, getStlScanList]);

	useEffect(() => {
		const _gridHeight = getAgGridHeight(scans.length);
		setGridHeight(_gridHeight);
	}, [scans.length]);

	return (
		<div className="employee-dependent-container">
			<div className="employee-dependent-grid-container">
				<div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
					<AgGridReact
						ref={agGridRef}
						rowData={scans}
						onGridReady={handleAgGridReady}
						onFirstDataRendered={handleFirstDataRendered}
						animateRows
						defaultColDef={{
							filter: "agTextColumnFilter",
							floatingFilter: true,
							resizable: true
						}}
						enableCellChangeFlash={true}
						defaultColGroupDef={{ marryChildren: true }}
						frameworkComponents={{
							handleDateCellRenderer: handleDateCellRenderer,
							handleCustomerNameCellRenderer: handleCustomerNameCellRenderer,
							stlLinksCellRenderer: StlLinksCellRenderer
						}}
						rowSelection={"single"}
					>
						<AgGridColumn field="customer_id" headerName="Customer Id" suppressSizeToFit></AgGridColumn>
						<AgGridColumn
							headerName="Customer Name"
							cellRenderer="handleCustomerNameCellRenderer"
							filter={false}
							suppressSizeToFit
						></AgGridColumn>
						<AgGridColumn field="phone" headerName="Phone Number" suppressSizeToFit></AgGridColumn>
						<AgGridColumn field="group_name" headerName="Group Name" suppressSizeToFit></AgGridColumn>
						<AgGridColumn field="store_name" headerName="Store Name" suppressSizeToFit></AgGridColumn>
						<AgGridColumn
							field="date"
							headerName="Scan Date"
							cellRenderer="handleDateCellRenderer"
							filter={false}
							suppressSizeToFit
						></AgGridColumn>
						<AgGridColumn
							headerName="STL Files"
							cellRenderer="stlLinksCellRenderer"
							filter={false}
							suppressSizeToFit={true}
						></AgGridColumn>
					</AgGridReact>
				</div>
			</div>
			<div className="custom-pagination" id="custom-pagination">
				<CustomPagination pageCount={pageCount} currentPage={currentPage} handlePageChange={handlePageChange} />
			</div>
		</div>
	);
};

export default LabScans;
